import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Avatar,
  Box,
  Modal,
  IconButton,
  Typography,
  Button,
  Dialog,
  CircularProgress,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import TaskListItem from "../ActivityList/TaskListItem";
import TaskCreation from "./TaskCreation";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import styles from "../ActivityList/ActivityList.module.css";
import phone from "../../assets/images/phone.png";
import call from "../ActivityList/assets/call.svg";
import meetings from "../ActivityList/assets/meeting.svg";
import email from "../ActivityList/assets/email.svg";
import demoIcon from "../ActivityList/assets/demo.svg";
import {
  resetEditTaskSuccess,
  resetTaskSuccess,
  resetCommentStatuses,
  resetDeleteTaskSuccess,
} from "../../pages/app/Nurture/service/nurtureSlice";
import {
  resetTaskById,
  getTaskById,
  resetTaskUpdateSuccess,
  updateTaskStatus,
} from "../../pages/app/Tasks/service/tasksSlice";

const highlightKeyframes = `
  @keyframes highlightPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(17, 200, 155, 0.7);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(17, 200, 155, 0.3);
    }
    100% {
      box-shadow: 0 0 0 4px rgba(17, 200, 155, 0.3);
    }
  }
`;

const TaskDetails = (props: any) => {
  const {
    DeleteTask,
    selectedLeadData,
    employees,
    CreateComment,
    DeleteComment,
    EditComment,
    tasks,
    EditTask,
    CreateTask,
    taskTypes,
    setFromTaskEdit,
    setTaskForEditing,
    taskForEditing: propTaskForEditing,
    fromTaskEdit: propFromTaskEdit,
    setShowAddTask,
    showAddTask,
    initialTaskId,
    isInDrawer = false,
    onCloseDrawer,
  } = props;

  const dispatch = useDispatch();

  const tasksFromRedux = useSelector((state: any) => state.tasks?.tasksboard);

  const tasksData = useMemo(() => {
    if (tasksFromRedux !== null && tasksFromRedux !== undefined) {
      return Array.isArray(tasksFromRedux) ? tasksFromRedux : [];
    }
    if (tasks && Array.isArray(tasks)) {
      return tasks;
    }
    return [];
  }, [tasksFromRedux, tasks]);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    initialTaskId || null,
  );
  const [taskIdForComment, setTaskIdForComment] = useState<string | null>(
    initialTaskId || null,
  );
  const [localFromTaskEdit, setLocalFromTaskEdit] = useState<boolean>(false);
  const [localTaskForEditing, setLocalTaskForEditing] = useState<any>(null);
  const [localShowAddTask, setLocalShowAddTask] = useState<boolean>(false);
  const [showEditChoiceModal, setShowEditChoiceModal] =
    useState<boolean>(false);
  const [editChoice, setEditChoice] = useState<"only" | "series" | null>(null);
  const [taskForEditChoice, setTaskForEditChoice] = useState<any>(null);
  const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(
    null,
  );

  const fromTaskEdit =
    propFromTaskEdit !== undefined ? propFromTaskEdit : localFromTaskEdit;
  const taskForEditing =
    propTaskForEditing !== undefined ? propTaskForEditing : localTaskForEditing;

  const {
    editTaskSuccess,
    createTaskSuccess,
    updateTaskStatusSuccess,
    createCommentSuccess,
    updateCommentSuccess,
    deleteCommentSuccess,
    lastCreatedComment,
    deleteTaskSuccess,
    lastCreatedTaskId,
  } = useSelector((state: any) => state.nurture);

  const { taskById, taskByIdLoading, taskStatusUpdateSuccess } = useSelector(
    (state: any) => state.tasks,
  );

  const handleUpdateTaskStatus = (body: any) => {
    dispatch(updateTaskStatus(body) as any);
  };

  const generalTasks = useMemo(() => {
    if (
      initialTaskId &&
      taskById &&
      String(taskById.id) === String(initialTaskId)
    ) {
      return [taskById];
    }

    if (!Array.isArray(tasksData)) return [];

    const allTasks: any[] = [];

    if (tasksData.length > 0 && tasksData[0]?.tasks?.data) {
      tasksData.forEach((col: any) => {
        if (Array.isArray(col?.tasks?.data)) {
          allTasks.push(...col.tasks.data);
        }
      });
    } else {
      allTasks.push(...tasksData);
    }

    const filteredTasks = allTasks.filter(
      (task: any) => task?.taskCategory?.toUpperCase() === "GENERAL_TASK",
    );

    const mappedTasks = filteredTasks.map((task: any) => {
      const comments = task.commentList || [];

      return {
        ...task,
        commentList: comments,
      };
    });

    return mappedTasks;
  }, [tasksData, taskById, initialTaskId]);

  const displayedTasks = useMemo(() => {
    let tasksToDisplay = generalTasks;

    if (initialTaskId) {
      const taskIdStr = String(initialTaskId);
      tasksToDisplay = generalTasks.filter((task: any) => {
        const taskId = task.id ? String(task.id) : null;
        return taskId === taskIdStr;
      });
    }

    const currentSelectedId = selectedTaskId || initialTaskId;
    if (
      taskById &&
      taskById.id &&
      currentSelectedId &&
      String(taskById.id) === String(currentSelectedId)
    ) {
      const existingTaskIndex = tasksToDisplay.findIndex(
        (task: any) => String(task.id) === String(taskById.id),
      );

      if (existingTaskIndex >= 0) {
        tasksToDisplay = tasksToDisplay.map((task: any) => {
          if (String(task.id) === String(taskById.id)) {
            const hasNewData =
              taskById.commentList !== undefined ||
              Object.keys(taskById).some((key) => task[key] !== taskById[key]);

            if (hasNewData) {
              return {
                ...task,
                ...taskById,
                commentList: taskById.commentList || task.commentList || [],
              };
            }
          }
          return task;
        });
      } else {
        tasksToDisplay = [taskById];
      }
    }

    return tasksToDisplay;
  }, [generalTasks, initialTaskId, selectedTaskId, taskById]);

  const handleTaskClick = (taskId: string) => {
    if (initialTaskId && initialTaskId === taskId) {
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
        setTaskIdForComment(null);
      } else {
        setSelectedTaskId(taskId);
        setTaskIdForComment(taskId);
      }
    } else if (!initialTaskId) {
      if (selectedTaskId === taskId) {
        setSelectedTaskId(null);
        setTaskIdForComment(null);
      } else {
        setSelectedTaskId(taskId);
        setTaskIdForComment(taskId);
      }
    }
  };

  const handleTaskIdForCommentChange = (taskId: string | null) => {
    setTaskIdForComment(taskId);
    if (taskId) {
      setSelectedTaskId(taskId);
    } else {
      setSelectedTaskId(null);
    }
  };

  const handleSetFromTaskEdit = (value: boolean) => {
    setLocalFromTaskEdit(value);
    if (setFromTaskEdit) setFromTaskEdit(value);
  };

  const handleSetTaskForEditing = (taskData: any) => {
    setLocalTaskForEditing(taskData);
    if (setTaskForEditing) setTaskForEditing(taskData);
  };

  const handleSetShowAddTask = (value: boolean) => {
    setLocalShowAddTask(value);
    if (setShowAddTask) setShowAddTask(value);
  };

  const handleGeneralTaskEdit = (taskData: any) => {
    if (taskData?.isRecurring === false) {
      handleSetTaskForEditing(taskData);
      handleSetFromTaskEdit(true);
      handleSetShowAddTask(true);
      setEditChoice(null);
      return;
    }

    const isRecurring = !!(
      taskData?.recurringTaskId ||
      taskData?.recurrence ||
      taskData?.recurringInfo ||
      taskData?.isRecurring
    );

    if (isRecurring) {
      setTaskForEditChoice(taskData);
      setShowEditChoiceModal(true);
    } else {
      handleSetTaskForEditing(taskData);
      handleSetFromTaskEdit(true);
      handleSetShowAddTask(true);
      setEditChoice(null);
    }
  };

  const handleEditOnlyThisChoice = () => {
    setEditChoice("only");
    setShowEditChoiceModal(false);
    if (taskForEditChoice) {
      handleSetTaskForEditing(taskForEditChoice);
      handleSetFromTaskEdit(true);
      handleSetShowAddTask(true);
    }
    setTaskForEditChoice(null);
  };

  const handleEditSeriesChoice = () => {
    setEditChoice("series");
    setShowEditChoiceModal(false);
    if (taskForEditChoice) {
      handleSetTaskForEditing(taskForEditChoice);
      handleSetFromTaskEdit(true);
      handleSetShowAddTask(true);
    }
    setTaskForEditChoice(null);
  };

  const handleCancelEditChoice = () => {
    setShowEditChoiceModal(false);
    setEditChoice(null);
    setTaskForEditChoice(null);
  };

  useEffect(() => {
    if (propTaskForEditing) {
      setLocalTaskForEditing(propTaskForEditing);
    }
    if (propFromTaskEdit !== undefined) {
      setLocalFromTaskEdit(propFromTaskEdit);
    }
  }, [propTaskForEditing, propFromTaskEdit]);

  useEffect(() => {
    const hasTask = localTaskForEditing || propTaskForEditing;
    const isEditing = localFromTaskEdit || propFromTaskEdit;

    if (hasTask && isEditing && !showAddTask) {
      handleSetShowAddTask(true);
    }
  }, [
    localTaskForEditing,
    propTaskForEditing,
    localFromTaskEdit,
    propFromTaskEdit,
  ]);

  useEffect(() => {
    if (!showAddTask && !localShowAddTask) {
      setLocalFromTaskEdit(false);
      setLocalTaskForEditing(null);
    } else if (showAddTask) {
      setLocalShowAddTask(true);
    }
  }, [showAddTask]);

  useEffect(() => {
    if (!initialTaskId) {
      dispatch(resetTaskById());
    }
  }, [initialTaskId, dispatch]);

  useEffect(() => {
    if (editTaskSuccess && editTaskSuccess !== false) {
      const capturedEditChoice = editChoice;
      const wasEditingEntireSeries = capturedEditChoice === "series";
      const wasEditingSingleTask = capturedEditChoice === "only";

      handleSetShowAddTask(false);
      handleSetFromTaskEdit(false);
      handleSetTaskForEditing(null);
      setEditChoice(null);

      if (isInDrawer) {
        setTimeout(() => {
          setSelectedTaskId(null);
          setTaskIdForComment(null);
          if (onCloseDrawer) {
            onCloseDrawer();
          }
        }, 100);
      }

      setTimeout(() => dispatch(resetEditTaskSuccess()), 3000);
    }
  }, [
    editTaskSuccess,
    dispatch,
    isInDrawer,
    selectedTaskId,
    initialTaskId,
    editChoice,
    taskForEditing,
    onCloseDrawer,
  ]);

  useEffect(() => {
    if (
      createTaskSuccess !== undefined &&
      createTaskSuccess !== false &&
      createTaskSuccess !== true
    ) {
      if (lastCreatedTaskId) {
        setTimeout(() => {
          setHighlightedTaskId(lastCreatedTaskId);

          const scrollToTask = (retries = 0) => {
            const taskElement = document.getElementById(
              `task-${lastCreatedTaskId}`,
            );
            if (taskElement) {
              taskElement.scrollIntoView({
                behavior: "auto",
                block: "nearest",
                inline: "nearest",
              });

              setTimeout(() => {
                const scrollableParent =
                  taskElement.closest('[style*="overflow"]') ||
                  taskElement.closest('[style*="overflowY"]') ||
                  document.querySelector('[style*="overflowY: auto"]');

                if (scrollableParent) {
                  const elementRect = taskElement.getBoundingClientRect();
                  const parentRect = scrollableParent.getBoundingClientRect();
                  const scrollTop = scrollableParent.scrollTop;

                  const elementCenter =
                    elementRect.top + elementRect.height / 2;
                  const parentCenter = parentRect.top + parentRect.height / 2;
                  const offset = elementCenter - parentCenter;
                  const targetScrollTop = scrollTop + offset;

                  scrollableParent.scrollTo({
                    top: Math.max(0, targetScrollTop),
                    behavior: "smooth",
                  });
                } else {
                  taskElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "nearest",
                  });
                }
              }, 100);
            } else if (retries < 10) {
              setTimeout(() => scrollToTask(retries + 1), 400);
            }
          };

          setTimeout(() => scrollToTask(), 500);

          setTimeout(() => {
            setHighlightedTaskId(null);
          }, 5000);
        }, 1200);
      }

      const currentTaskId = lastCreatedTaskId;
      const resetTimeout = setTimeout(() => {
        if (currentTaskId === lastCreatedTaskId) {
          dispatch(resetTaskSuccess());
        }
      }, 3000);

      return () => clearTimeout(resetTimeout);
    }
  }, [createTaskSuccess, dispatch, lastCreatedTaskId]);

  useEffect(() => {
    const isCommentSuccess =
      (createCommentSuccess !== false && createCommentSuccess !== true) ||
      (updateCommentSuccess !== false && updateCommentSuccess !== true) ||
      (deleteCommentSuccess !== false && deleteCommentSuccess !== true);

    if (isCommentSuccess) {
      const currentTaskId = selectedTaskId || initialTaskId;
      if (
        currentTaskId &&
        initialTaskId &&
        String(currentTaskId) === String(initialTaskId)
      ) {
        dispatch(
          getTaskById({
            id: String(currentTaskId),
            taskCategory: "GENERAL_TASK",
          }) as any,
        ).catch((error: any) => {
          if (error?.status !== 404) {
            console.error("Error fetching task by ID:", error);
          } else if (isInDrawer && onCloseDrawer) {
            setSelectedTaskId(null);
            setTaskIdForComment(null);
            onCloseDrawer();
          }
        });
      }

      const isDelete =
        deleteCommentSuccess !== false && deleteCommentSuccess !== true;
      const delay = isDelete ? 3000 : 2000;

      setTimeout(() => {
        dispatch(resetCommentStatuses());
      }, delay);
    }
  }, [
    createCommentSuccess,
    updateCommentSuccess,
    deleteCommentSuccess,
    dispatch,
    selectedTaskId,
    initialTaskId,
    isInDrawer,
    onCloseDrawer,
  ]);

  useEffect(() => {
    const isStatusUpdateSuccess =
      taskStatusUpdateSuccess !== false && taskStatusUpdateSuccess !== true;

    if (isStatusUpdateSuccess) {
      const currentTaskId = selectedTaskId || initialTaskId;
      if (
        currentTaskId &&
        initialTaskId &&
        String(currentTaskId) === String(initialTaskId)
      ) {
        dispatch(
          getTaskById({
            id: String(currentTaskId),
            taskCategory: "GENERAL_TASK",
          }) as any,
        )
          .then((result: any) => {
            if (isInDrawer && onCloseDrawer) {
              setTimeout(() => {
                setSelectedTaskId(null);
                setTaskIdForComment(null);
                onCloseDrawer();
              }, 800);
            }
          })
          .catch((error: any) => {
            if (error?.status !== 404) {
              console.error("Error fetching task by ID:", error);
            }

            if (isInDrawer && onCloseDrawer) {
              setTimeout(() => {
                setSelectedTaskId(null);
                setTaskIdForComment(null);
                onCloseDrawer();
              }, 800);
            }
          });
      }

      setTimeout(() => {
        dispatch(resetTaskUpdateSuccess());
      }, 2000);
    }
  }, [
    taskStatusUpdateSuccess,
    dispatch,
    selectedTaskId,
    initialTaskId,
    isInDrawer,
    onCloseDrawer,
  ]);

  useEffect(() => {
    if (deleteTaskSuccess && deleteTaskSuccess !== false) {
      if (isInDrawer && onCloseDrawer) {
        setTimeout(() => {
          setSelectedTaskId(null);
          setTaskIdForComment(null);
          onCloseDrawer();
        }, 100);
      }

      setTimeout(() => {
        dispatch(resetDeleteTaskSuccess());
      }, 100);
    }
  }, [deleteTaskSuccess, isInDrawer, onCloseDrawer, dispatch]);

  useEffect(() => {
    if (initialTaskId) {
      const taskIdStr = String(initialTaskId);

      if (selectedTaskId !== taskIdStr) {
        setSelectedTaskId(taskIdStr);
        setTaskIdForComment(taskIdStr);
      }
    } else {
      if (selectedTaskId !== null) {
        setSelectedTaskId(null);
        setTaskIdForComment(null);
      }
    }
  }, [initialTaskId]);

  useEffect(() => {
    if (taskById && taskById.id) {
      const taskIdStr = String(taskById.id);
      const currentSelectedId = selectedTaskId || initialTaskId;

      if (currentSelectedId && String(currentSelectedId) === taskIdStr) {
      }
    }
  }, [taskById, selectedTaskId, initialTaskId]);

  const handleCancelClick = () => {
    handleSetShowAddTask(false);
    handleSetFromTaskEdit(false);
    handleSetTaskForEditing(null);
    setEditChoice(null);
  };

  const sideIcon = (type: string) => {
    switch (type) {
      case "CALL":
        return call;
      case "MEETING":
        return meetings;
      case "EMAIL":
        return email;
      case "DEMO":
        return demoIcon;
      default:
        return phone;
    }
  };

  return (
    <Box sx={{ backgroundColor: "#FFF" }}>
      <style>{highlightKeyframes}</style>
      {showEditChoiceModal && (
        <Dialog
          open={showEditChoiceModal}
          onClose={handleCancelEditChoice}
          sx={{
            zIndex: "9999999",
            "& .MuiDialog-paper": {
              width: "100%",
              maxWidth: "400px",
              padding: "34px",
              borderRadius: "8px",
              boxShadow:
                "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
              background: "#FFFFFF",
              position: "relative",
            },
          }}
        >
          <IconButton
            onClick={handleCancelEditChoice}
            sx={{
              position: "absolute",
              top: "22px",
              right: "12px",
              color: "#667085",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              marginBottom: "24px",
              gap: "12px",
            }}
          >
            <EditIcon
              sx={{
                fontSize: "24px",
                color: "#11c89b",
                marginTop: "2px",
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <div
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#101828",
                  marginBottom: "8px",
                }}
              >
                Edit Task
              </div>
              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#101828",
                }}
              >
                Would you like to edit this task or the entire series?
              </div>
            </Box>
          </Box>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "12px",
              justifyContent: "space-between",
            }}
          >
            <Button
              sx={{
                textTransform: "capitalize",
                backgroundColor: "#11c89b",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#0fb889" },
                flex: 1,
              }}
              onClick={handleEditOnlyThisChoice}
            >
              Edit this task
            </Button>
            {!!(
              taskForEditChoice?.recurringTaskId ||
              taskForEditChoice?.recurrence ||
              taskForEditChoice?.recurringInfo ||
              taskForEditChoice?.isRecurring
            ) && (
                <Button
                  sx={{
                    textTransform: "capitalize",
                    backgroundColor: "#11c89b",
                    color: "#ffffff",
                    "&:hover": { backgroundColor: "#0fb889" },
                    flex: 1,
                  }}
                  onClick={handleEditSeriesChoice}
                >
                  Edit entire series
                </Button>
              )}
          </div>
        </Dialog>
      )}

      <Modal
        open={showAddTask || localShowAddTask || false}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCancelClick();
        }}
        aria-labelledby="edit-task-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: 24,
            width: { xs: "95%", sm: "80%", md: "1000px" },
            maxWidth: 900,
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              bgcolor: "background.paper",
              borderRadius: "8px 8px 0 0",
              p: 3,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography id="edit-task-modal" variant="h6" sx={{ ml: "20px" }}>
              Edit Task
            </Typography>
            <IconButton
              onClick={handleCancelClick}
              sx={{
                color: "#666",
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              overflowY: "auto",
              p: 3,
              pt: 0,
            }}
          >
            <TaskCreation
              CreateTask={CreateTask}
              employees={employees}
              leadById={selectedLeadData}
              taskTypes={taskTypes}
              handleCancelClick={handleCancelClick}
              fromTaskEdit={fromTaskEdit}
              taskForEditing={taskForEditing}
              EditTask={EditTask}
              onClose={handleCancelClick}
              editChoice={editChoice}
            />
          </Box>
        </Box>
      </Modal>

      {/* List */}
      <Box sx={{ height: "100%" }}>
        {taskByIdLoading && initialTaskId && displayedTasks.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: isInDrawer
                ? "calc(100vh - 140px)"
                : "calc(98vh - 200px)",
              background: "#fff",
            }}
          >
            <CircularProgress size={40} sx={{ color: "#11c89b" }} />
          </Box>
        ) : displayedTasks.length > 0 ? (
          <Box
            sx={{
              maxHeight: isInDrawer
                ? "calc(100vh - 140px)"
                : "calc(98vh - 200px)",
              overflowY: "auto",
              padding: isInDrawer ? "8px 16px" : "0",
            }}
          >
            {displayedTasks.map((item: any, index: number) => {
              const taskIdToMatch = initialTaskId
                ? String(initialTaskId)
                : selectedTaskId
                  ? String(selectedTaskId)
                  : null;
              const itemIdStr = item.id ? String(item.id) : null;
              const isSelected =
                taskIdToMatch && itemIdStr && taskIdToMatch === itemIdStr;
              const isHighlighted =
                highlightedTaskId &&
                String(item.id) === String(highlightedTaskId);

              return (
                <Box
                  key={item.id || index}
                  id={`task-${item.id}`}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (
                      target.closest("button") ||
                      target.closest('[class*="commentBtn"]') ||
                      target.closest('[class*="popup"]') ||
                      target.closest('[class*="edit"]') ||
                      target.closest('[role="tooltip"]') ||
                      target.tagName === "IMG" ||
                      target.closest("svg")
                    ) {
                      return;
                    }
                    handleTaskClick(item.id);
                  }}
                  sx={{
                    marginBottom: isInDrawer ? "6px" : "8px",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    borderRadius: "4px",
                    transition: "background-color 0.2s ease-in-out",
                    padding: isInDrawer
                      ? "0"
                      : isSelected
                        ? "8px 8px 8px 8px"
                        : "4px",
                    cursor: "pointer",
                    ...(isHighlighted && {
                      animation: "highlightPulse 2s ease-in-out",
                      boxShadow: "0 0 0 4px rgba(17, 200, 155, 0.3) !important",
                      border: "2px solid #11c89b !important",
                    }),
                  }}
                >
                  <Box
                    sx={{
                      marginTop: "16px",
                      marginRight: "8px",
                      flexShrink: 0,
                    }}
                  >
                    <Box
                      sx={{
                        height: "25px",
                        width: "25px",
                        borderRadius: "50%",
                        border: "1px solid #F0F0F0",
                        textAlign: "center",
                        backgroundColor: "#FFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                      }}
                    >
                      <div className={styles.activitySideIcon}>
                        <img
                          src={
                            item?.taskType === undefined ||
                              item?.taskType === ""
                              ? phone
                              : sideIcon(item?.taskType)
                          }
                          alt="?"
                        />
                      </div>
                    </Box>
                  </Box>
                  <TaskListItem
                    data={item}
                    DeleteTask={DeleteTask}
                    setFromTaskEdit={handleSetFromTaskEdit}
                    setTaskForEditing={handleSetTaskForEditing}
                    setShowAddTask={(value: boolean) => {
                      handleSetShowAddTask(value);
                    }}
                    onEditClick={handleGeneralTaskEdit}
                    showAddTask={showAddTask}
                    UpdateTaskStatus={handleUpdateTaskStatus}
                    employees={employees}
                    selectedLeadData={selectedLeadData}
                    updateTaskStatusSuccess={updateTaskStatusSuccess}
                    CreateComment={CreateComment}
                    DeleteComment={DeleteComment}
                    EditComment={EditComment}
                    leadById={selectedLeadData}
                    taskIdForComment={isSelected ? String(item.id) : null}
                    setTaskIdForComment={handleTaskIdForCommentChange}
                    listView={isSelected ? false : true}
                    fromSideDrawer={isInDrawer}
                    isGeneralTask={true}
                  />
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: "center",
              padding: isInDrawer ? "30px 16px" : "40px",
              color: "#142C44",
              fontSize: isInDrawer ? "13px" : "14px",
              background: "#fff",
              maxHeight: isInDrawer
                ? "calc(100vh - 140px)"
                : "calc(98vh - 200px)",
              minHeight: isInDrawer
                ? "calc(100vh - 140px)"
                : "calc(98vh - 200px)",
              overflowY: "auto",
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  color: "#142C44",
                  fontSize: isInDrawer ? "13px" : "14px",
                  fontWeight: "400",
                  lineHeight: isInDrawer ? "20px" : "22px",
                  marginBottom: isInDrawer ? "16px" : "20px",
                }}
              >
                {initialTaskId
                  ? "Task not found. It may have been deleted or you may not have access to view it."
                  : "No general tasks available."}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TaskDetails;
